/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useEffect, useState } from 'react';
import { TimeSeries, TimeRange } from 'pondjs';

import {
  Charts,
  ChartContainer,
  ChartRow,
  YAxis,
  LineChart,
  styler,
  ScatterChart,
  BandChart,
  Resizable,
} from 'react-timeseries-charts';
import { makeStyles, FormControlLabel, Switch } from '@material-ui/core';
import moment from 'moment';
import { promStringify } from '../../../shared/utils/prometheus';
import Loading from '../../../shared/components/Loading';
import { roundAndExponent } from '../../../shared/utils/math';
import MonitoringTooltip from './MonitoringTooltip';
import { getAnalyticsTimeseries } from '../../../shared/services/analyticsTimeseries';
import { anomalyColor, bandChartColor } from '../../../shared/styles/colors';

const useStyles = makeStyles((theme) => ({
  iconsRow: {
    height: '39px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  chartContainer: {
    width: '100%',
    cursor: 'crosshair',
  },
  linkButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    userSelect: 'text',
    textDecoration: 'none',
    outline: '0',
    display: 'grid',
    gridTemplateAreas: `
      'bar text'
`,
    gridTemplateColumns: '40px auto',
    columnGap: theme.spacing(1),
  },
  metricLabelBar: {
    gridArea: 'bar',
    alignSelf: 'center',
    width: '100%',
    height: '5px',
  },
  metricLabelText: {
    gridArea: 'text',
    wordBreak: 'break-all',
  },
  chartContainerDiv: {
    position: 'relative',
  },
}));

const Y_AXIS_ID = 'yaxis';
const Y_AXIS_WIDTH = 50;
const Y_AXIS_OFFSET = 0;
const CHART_HEIGHT = 125;
const AXIS_MARGIN = 5;
const BAND_CHART_COLUMNS = ['index', 'bounds'];
const ANOMALY_SCATTER_CHART_COLUMNS = ['time', 'anomaly'];

const validPointForLogScale = (point: number | null) => (point !== null && point <= 0 ? null : point);

const createLineChartData = (name: string, columns: string[], points: unknown[][], isLog: boolean) =>
  new TimeSeries({
    name,
    columns,
    points: !isLog
      ? points
      : points.map((point) => {
          const newPoint = [...point];
          newPoint[1] = validPointForLogScale(newPoint[1] as number);
          return newPoint;
        }),
  });

const getChartMinMax = (
  chartData: TimeSeries,
  timeRange: TimeRange,
  isLog: boolean,
  columns: string[]
): Record<string, number> => {
  const timeSeriesInView = typeof timeRange !== 'undefined' ? chartData.crop(timeRange) : chartData;
  let max =
    columns.length <= 1
      ? timeSeriesInView.max(columns[1])
      : columns
          .slice(3, 5)
          .reduce((a, column) => Math.max(a, timeSeriesInView.max(column)), timeSeriesInView.max(columns[1]));
  let min =
    columns.length <= 1
      ? timeSeriesInView.min(columns[1], (values: number[]) => values)
      : columns.slice(3, 5).reduce(
          (a, column) =>
            Math.min(
              a,
              timeSeriesInView.min(column, (values: number[]) => values)
            ),
          timeSeriesInView.min(columns[1], (values: number[]) => values)
        );

  if (typeof max === 'undefined') {
    max = 1000;
  }
  if (typeof min === 'undefined') {
    min = 0;
  }

  // Can't have 0 values in log scale
  if (isLog) {
    if (max <= 0) {
      max = 1000;
    }
    if (min <= 0) {
      min = 1;
    }
  }
  return { max, min };
};

const createBandChartData = (
  name: string,
  lowerPoints: (number | string | null)[][],
  upperPoints: (number | string | null)[][],
  isLog: boolean
) => {
  return new TimeSeries({
    name,
    columns: BAND_CHART_COLUMNS,
    points: lowerPoints.map((dataInfo, it) => {
      const time = '1s-'.concat(((dataInfo[0] as number) / 1000).toString());
      const lowerBound = !isLog ? dataInfo[1] : validPointForLogScale(dataInfo[1] as number);
      const upperBound = !isLog ? upperPoints[it][1] : validPointForLogScale(upperPoints[it][1] as number);
      // Band chart is not drawn properly in case bounds are null. So making a 0 band (upper - lower = 0).
      return lowerBound !== null && upperBound !== null ? [time, [lowerBound, upperBound]] : [time, [1, 1]];
    }),
  });
};

const createAnomalyScatterChartData = (
  name: string,
  metricsData: (number | null)[][],
  isAnomaly: (number | string | null)[][],
  isLog: boolean
) => {
  return new TimeSeries({
    name,
    columns: ANOMALY_SCATTER_CHART_COLUMNS,
    points: metricsData.map((dataInfo, it) => {
      let point: (number | string | null)[];
      if (it < isAnomaly.length && isAnomaly[it][1] === 1) {
        point = dataInfo;
        if (isLog) {
          point[1] = validPointForLogScale(point[1] as number | null);
        }
      } else {
        point = [dataInfo[0], null];
      }
      return point;
    }),
  });
};

interface MonitoringTimeseriesChartProps {
  metricsData: Record<string, (number | null)[][]>;
  selected?: string;
  columnDefs: string[];
  chartColors: string[];
  showLogScale?: boolean;
}

const MonitoringTimeseriesChart: React.FC<MonitoringTimeseriesChartProps> = ({
  metricsData,
  selected = undefined,
  columnDefs,
  chartColors,
  showLogScale = true,
}: MonitoringTimeseriesChartProps) => {
  // Chart details
  const [chartMax, setChartMax] = useState<number>();
  const [chartMin, setChartMin] = useState<number>();
  const [minMaxTimeRange, setMinMaxTimeRange] = useState<TimeRange>();
  const [timeRange, setTimeRange] = useState<TimeRange>();
  const [timeSeriesData, setTimeSeriesData] = useState<Record<string, TimeSeries>>({});
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>(selected);
  const [bandChartData, setBandChartData] = useState<TimeSeries>();
  const [anomalyChartData, setAnomalyChartData] = useState<TimeSeries>();

  // Tracker/tooltip details
  const [dateTracker, setDateTracker] = useState<Date>();
  const [trackerX, setTrackerX] = useState<number>();
  const [trackerY, setTrackerY] = useState<number>();
  const [mouseIn, setMouseIn] = useState<boolean>(false);

  // LineChart scale type
  const [isLogScale, setIsLogScale] = useState<boolean>(false);
  const [scaleType, setScaleType] = useState<string>('linear');

  const selectorToColorMapping: Record<string, string> = {};
  Object.entries(metricsData).forEach(([selector], index) => {
    selectorToColorMapping[selector] = chartColors[index];
  });

  const resetAllChartData = () => {
    setTimeSeriesData({});
    setTimeRange(undefined);
    setChartMin(undefined);
    setChartMax(undefined);
  };

  useEffect(() => {
    setScaleType(isLogScale ? 'log' : 'linear');
    resetAllChartData();
  }, [isLogScale]);

  useEffect(() => {
    setMinMaxTimeRange(timeRange);
  }, [timeRange]);

  useEffect(() => {
    const allTimeSeriesData: Record<string, TimeSeries> = {};
    Object.entries(metricsData).forEach(([selector, value]) => {
      if (typeof selectedMetric === 'undefined' || selectedMetric === selector) {
        allTimeSeriesData[selector] = createLineChartData(selector, columnDefs, value, scaleType === 'log');
      }
    });
    setTimeSeriesData(allTimeSeriesData);
  }, [scaleType, columnDefs, metricsData, selectedMetric]);

  useEffect(() => {
    let aggregateTimeRange: TimeRange | undefined;
    Object.entries(timeSeriesData).forEach(([, timeseries]) => {
      if (typeof timeseries.timerange() !== 'undefined') {
        if (typeof aggregateTimeRange === 'undefined') {
          aggregateTimeRange = timeseries.timerange();
        } else {
          aggregateTimeRange = aggregateTimeRange.extents(timeseries.timerange());
        }
      }
    });
    if (aggregateTimeRange) {
      // Extend the timeRange by 1 min because many often the range is [start, end[. Add 1 min to make it [start, min]
      aggregateTimeRange = aggregateTimeRange.setEnd(moment(aggregateTimeRange.end()).add(1, 'm').toDate());
    }
    setTimeRange(aggregateTimeRange);
  }, [timeSeriesData]);

  useEffect(() => {
    if (typeof selectedMetric !== 'undefined' && typeof timeRange !== 'undefined') {
      const parsedSelectedMetric = JSON.parse(selectedMetric) as Record<string, string>;
      const selectedMetricName = parsedSelectedMetric.__name__;
      delete parsedSelectedMetric.__name__;
      void (async (): Promise<void> => {
        const response = await getAnalyticsTimeseries(
          timeRange.begin().toISOString(),
          timeRange.end().toISOString(),
          selectedMetricName,
          parsedSelectedMetric
        );
        if (response && response.length > 0) {
          setBandChartData(createBandChartData(selectedMetric, response[0].low, response[0].high, isLogScale));
          setAnomalyChartData(
            createAnomalyScatterChartData(
              selectedMetric,
              metricsData[selectedMetric],
              response[0].isAnomaly,
              isLogScale
            )
          );
        }
      })();
    } else {
      setBandChartData(undefined);
      setAnomalyChartData(undefined);
    }
  }, [selectedMetric, isLogScale, metricsData, timeRange]);

  useEffect(() => {
    let minimumValue = Number.MAX_VALUE;
    let maximumValue = Number.MIN_VALUE;
    Object.entries(timeSeriesData).forEach(([, timeseries]) => {
      if (typeof minMaxTimeRange !== 'undefined') {
        const { min, max } = getChartMinMax(timeseries, minMaxTimeRange, scaleType === 'log', columnDefs);
        minimumValue = Math.min(min, minimumValue);
        maximumValue = Math.max(max, maximumValue);
      }
    });
    setChartMax(maximumValue);
    setChartMin(minimumValue);
  }, [timeSeriesData, scaleType, minMaxTimeRange, columnDefs]);

  const classes = useStyles();
  return (
    <div className={classes.chartContainer}>
      {Object.keys(timeSeriesData).length > 0 && typeof minMaxTimeRange !== 'undefined' ? (
        <div className={classes.chartContainer}>
          {showLogScale && (
            <div className={classes.iconsRow}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isLogScale}
                    onChange={(event) => {
                      setIsLogScale(event.target.checked);
                    }}
                    color="default"
                    size="small"
                  />
                }
                label={isLogScale ? 'log' : 'linear'}
              />
            </div>
          )}
          <div className={classes.chartContainerDiv}>
            {typeof trackerX !== 'undefined' &&
              typeof trackerY !== 'undefined' &&
              typeof dateTracker !== 'undefined' &&
              typeof chartMin !== 'undefined' &&
              typeof chartMax !== 'undefined' && (
                <MonitoringTooltip
                  xPosition={trackerX}
                  yPosition={trackerY}
                  tracker={dateTracker}
                  chartData={timeSeriesData}
                  selectorToColorMapping={selectorToColorMapping}
                  isLogScale={isLogScale}
                  height={CHART_HEIGHT}
                  axisMargin={AXIS_MARGIN}
                  min={chartMin}
                  max={chartMax}
                  bandChartData={bandChartData}
                />
              )}
            <Resizable
              onMouseEnter={() => {
                setMouseIn(true);
              }}
              onMouseLeave={() => {
                setMouseIn(false);
              }}
            >
              <ChartContainer
                timeRange={minMaxTimeRange}
                enableDragZoom
                onTimeRangeChanged={(timerange: TimeRange) => {
                  setMinMaxTimeRange(timerange);
                }}
                trackerPosition={dateTracker}
                onTrackerChanged={(newTracker: Date | null) => {
                  if (newTracker) {
                    setDateTracker(new Date(newTracker));
                  } else {
                    setDateTracker(undefined);
                  }
                }}
                onMouseMove={(x0: number, y0: number) => {
                  setTrackerX(Y_AXIS_WIDTH + Y_AXIS_OFFSET + x0);
                  setTrackerY(y0);
                }}
                timeAxisTickCount={0}
              >
                <ChartRow height={CHART_HEIGHT} axisMargin={AXIS_MARGIN}>
                  {typeof chartMax !== 'undefined' && typeof chartMin !== 'undefined' && (
                    <YAxis
                      id={Y_AXIS_ID}
                      min={chartMin}
                      max={chartMax}
                      width={Y_AXIS_WIDTH}
                      labelOffset={Y_AXIS_OFFSET}
                      type={scaleType}
                      format={roundAndExponent}
                    />
                  )}
                  <Charts>
                    {Object.entries(timeSeriesData).map(([key, value]) => (
                      <LineChart
                        axis={Y_AXIS_ID}
                        key={key}
                        series={value}
                        columns={[columnDefs[1]]}
                        style={styler([{ key: 'value', color: selectorToColorMapping[key], width: 1 }])}
                      />
                    ))}
                    <BandChart
                      visible={typeof bandChartData !== 'undefined'}
                      axis={Y_AXIS_ID}
                      style={styler([
                        {
                          key: BAND_CHART_COLUMNS[1],
                          color: bandChartColor,
                          width: 1,
                          opacity: 1,
                        },
                      ])}
                      spacing={1}
                      interpolation="curveLinear"
                      column={BAND_CHART_COLUMNS[1]}
                      series={bandChartData}
                    />
                    {Object.entries(timeSeriesData).map(([key, value]) => (
                      <ScatterChart
                        radius={2.5}
                        key={key}
                        visible={mouseIn}
                        axis={Y_AXIS_ID}
                        series={value}
                        columns={[columnDefs[1]]}
                        style={styler([{ key: 'value', color: selectorToColorMapping[key], width: 1 }])}
                      />
                    ))}
                    <ScatterChart
                      radius={2.5}
                      visible={typeof anomalyChartData !== 'undefined'}
                      axis={Y_AXIS_ID}
                      series={anomalyChartData}
                      columns={[ANOMALY_SCATTER_CHART_COLUMNS[1]]}
                      style={styler([{ key: ANOMALY_SCATTER_CHART_COLUMNS[1], color: anomalyColor, width: 1 }])}
                    />
                  </Charts>
                </ChartRow>
              </ChartContainer>
            </Resizable>
          </div>
          <div>
            {Object.entries(metricsData).map(([key], index) => (
              <div
                key={key}
                style={{ opacity: typeof selectedMetric === 'undefined' || selectedMetric === key ? '1' : '0.5' }}
              >
                <button
                  type="button"
                  className={classes.linkButton}
                  onClick={() => {
                    if (typeof selectedMetric === 'undefined') {
                      setSelectedMetric(key);
                    } else {
                      setSelectedMetric(undefined);
                    }
                  }}
                >
                  <div
                    className={classes.metricLabelBar}
                    style={{
                      backgroundColor: chartColors[index],
                    }}
                  />
                  <div className={classes.metricLabelText}>
                    {promStringify(JSON.parse(key) as Record<string, string>)}
                  </div>
                </button>
                <br />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default MonitoringTimeseriesChart;
